import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ButtonLink,
  ButtonRound,
  CloseIcon,
  FormBox,
  InputCheckbox,
  InputRadio,
  InputText,
  Notice,
  PageActions,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { usePageHeader } from "@components/ow";
import {
  QUESTION_TYPES,
  QUIZ_QUESTION_MAX,
  QUIZ_TITLE_MAX,
  emptyOption,
  emptyQuestion,
  emptyQuiz,
  hydrateQuiz,
  quizFieldId,
  quizPayload,
  validateQuiz,
} from "@helpers/quizForm";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";

// Formulário de questionário (modelo Formulário do DS OneWork), usado pelo novo
// (/c/quiz/new) e pelo editar (/c/quiz/:id). Sem `quizId` cria; com ele carrega o
// questionário e salva por cima. Uma FormBox para o questionário e uma por pergunta.

const LIST_HREF = "/c/quiz";

const Stack = styled.div`
  && {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  fieldset {
    margin-bottom: 0;
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Opção: o texto ocupa o resto da linha; "Correta" e o remover ficam na altura do campo
const OptionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 40px;
  align-items: center;
  column-gap: 20px;

  fieldset {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const AddQuestion = styled.div`
  display: flex;
`;

function GroupError({ message }) {
  return message ? <Text $text={message} $variant="caption_medium" $color="E400" /> : null;
}

/** O foco do erro do tipo vai para a primeira opção do rádio (a lib monta o id `nome_valor`). */
const focusId = (id) => (id.endsWith("-type") ? `${id}_${QUESTION_TYPES[0].value}` : id);

export default function QuizForm({ quizId }) {
  const isEdit = !!quizId;
  const quizService = useService("quiz");
  const navigate = useNavigate();
  const [loadingData, setLoadingData] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(emptyQuiz);
  const [respondents, setRespondents] = useState(0);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);
  // Pergunta recém-adicionada: o foco vai para ela depois de renderizar
  const [focusOnRender, setFocusOnRender] = useState(null);

  const title = isEdit ? "Editar questionário" : "Novo questionário";
  usePageHeader({
    title,
    breadcrumb: [
      { page: "Questionários", route: LIST_HREF },
      { page: title, route: isEdit ? `${LIST_HREF}/${quizId}` : `${LIST_HREF}/new` },
    ],
  });

  useEffect(() => {
    if (isEdit) fetchQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focusOnRender) return;
    const el = document.getElementById(focusOnRender);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
    setFocusOnRender(null);
  }, [focusOnRender]);

  async function fetchQuiz() {
    setLoadingData(true);
    try {
      const result = await quizService.get(quizId);
      if (!result?.data) throw new Error("Questionário não encontrado");
      setValues(hydrateQuiz(result.data));
      setRespondents(Number(result.data.totalRespondents) || 0);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao carregar o questionário");
      navigate(LIST_HREF);
      return;
    }
    setLoadingData(false);
  }

  const setField = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

  const updateQuestion = (key, patch) =>
    setValues((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.key === key ? { ...q, ...patch } : q)),
    }));

  const updateOption = (question, optionKey, patch) =>
    updateQuestion(question.key, {
      options: question.options.map((o) => (o.key === optionKey ? { ...o, ...patch } : o)),
    });

  function addQuestion() {
    const question = emptyQuestion();
    setValues((prev) => ({ ...prev, questions: [...prev.questions, question] }));
    setFocusOnRender(quizFieldId.description(question));
  }

  const removeQuestion = (key) =>
    setValues((prev) => ({ ...prev, questions: prev.questions.filter((q) => q.key !== key) }));

  function addOption(question) {
    const option = emptyOption();
    updateQuestion(question.key, { options: [...question.options, option] });
    setFocusOnRender(quizFieldId.option(question, option));
  }

  const removeOption = (question, optionKey) =>
    updateQuestion(question.key, { options: question.options.filter((o) => o.key !== optionKey) });

  const fieldErrors = submitted ? validateQuiz(values) : {};

  async function handleSubmit() {
    setSubmitted(true);
    const errors = validateQuiz(values);
    if (Object.keys(errors).length) {
      focusFirstError(Object.keys(errors).map(focusId));
      return;
    }

    setSaving(true);
    // Em erro, o service já mostra o toast com a mensagem do back e devolve { error: true }
    const result = await quizService.save(quizPayload(values), quizId);
    setSaving(false);
    if (!result || result.error) return;

    toast.success(isEdit ? "Questionário atualizado" : "Questionário criado");
    navigate(LIST_HREF);
  }

  if (loadingData) return <FormPageSkeleton sections={[150, 260, 260]} />;

  function renderOptions(question) {
    return (
      <FullWidth>
        <Options>
          {question.options.map((option, index) => {
            const optionId = quizFieldId.option(question, option);
            const correctId = `${optionId}-correct`;
            return (
              <OptionRow key={option.key}>
                <InputText
                  $idForm={optionId}
                  $label={`Opção ${index + 1}`}
                  $variant="default"
                  $isRequired
                  $errors={fieldErrors[optionId]}
                  value={option.title}
                  onChange={(e) => updateOption(question, option.key, { title: e.target.value })}
                />
                {/* O checkbox da lib não é controlado: avisa a mudança no input e no fieldset */}
                <InputCheckbox
                  $options={[{ name: correctId, label: "Correta", value: option.isCorrect }]}
                  onChange={(e) =>
                    e.target.name === correctId && updateOption(question, option.key, { isCorrect: e.target.checked })
                  }
                />
                {question.options.length > 1 ? (
                  <ButtonRound
                    title="Remover opção"
                    aria-label={`Remover a opção ${index + 1}`}
                    $variant="outline"
                    $icon={<CloseIcon />}
                    $noWidth
                    $action={() => removeOption(question, option.key)}
                  />
                ) : (
                  <span />
                )}
              </OptionRow>
            );
          })}
        </Options>
        <Stack id={quizFieldId.options(question)}>
          <div>
            <ButtonLink $text="Adicionar opção" $action={() => addOption(question)} $noPadding $noWidth />
          </div>
          <GroupError message={fieldErrors[quizFieldId.options(question)]} />
        </Stack>
      </FullWidth>
    );
  }

  function renderRemoveQuestion(question) {
    const hasAnswers = question.answersCount > 0;
    const button = (
      <ButtonLink
        $text="Remover pergunta"
        $color="E400"
        $isDisabled={hasAnswers || values.questions.length === 1}
        $action={() => removeQuestion(question.key)}
        $noPadding
        $noWidth
      />
    );
    return (
      <Footer>
        {hasAnswers ? (
          <Tooltip
            content="Esta pergunta já tem respostas e não pode ser removida."
            $position="top"
            $size="medium"
            $color="dark"
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </Footer>
    );
  }

  return (
    <FormPage
      notice={
        isEdit && respondents > 0 ? (
          <Notice
            $variant="orange"
            $title={`${respondents} ${respondents === 1 ? "pessoa já respondeu" : "pessoas já responderam"}`}
            $text="As respostas ficam ligadas a cada pergunta. Mudar o texto muda o que elas parecem responder, e a pergunta com resposta não pode ser removida."
          />
        ) : null
      }
      actions={
        <PageActions>
          <ButtonLink $text="Cancelar" $action={() => navigate(LIST_HREF)} />
          <Button
            $text={isEdit ? "Salvar alterações" : "Criar questionário"}
            $variant="primary"
            $action={handleSubmit}
            $isLoading={saving}
          />
        </PageActions>
      }
    >
      <FormBox title="Questionário">
        <FullWidth>
          <InputText
            $idForm={quizFieldId.title()}
            $label="Título"
            $variant="default"
            $isRequired
            $errors={fieldErrors[quizFieldId.title()]}
            maxLength={QUIZ_TITLE_MAX}
            placeholder="Ex.: Pesquisa de integração"
            value={values.title}
            onChange={(e) => setField("title", e.target.value)}
          />
        </FullWidth>
        <FullWidth>
          <Stack>
            <InputCheckbox
              $options={[{ name: "quiz-active", label: "Questionário ativo", value: values.active }]}
              onChange={(e) => e.target.name === "quiz-active" && setField("active", e.target.checked)}
            />
            <Text
              $text="Se marcado, os colaboradores veem e respondem o questionário."
              $variant="caption_medium"
              $color="N400"
            />
          </Stack>
        </FullWidth>
      </FormBox>

      {values.questions.map((question, index) => {
        const typeId = quizFieldId.type(question);
        return (
          <FormBox key={question.key} title={`Pergunta ${index + 1}`}>
            <FullWidth>
              <InputText
                $idForm={quizFieldId.description(question)}
                $label="Pergunta"
                $variant="default"
                $isRequired
                $errors={fieldErrors[quizFieldId.description(question)]}
                maxLength={QUIZ_QUESTION_MAX}
                placeholder="Ex.: Como foi a sua primeira semana?"
                value={question.description}
                onChange={(e) => updateQuestion(question.key, { description: e.target.value })}
              />
            </FullWidth>
            <FullWidth>
              <Stack>
                <InputRadio
                  name={typeId}
                  $legend="Tipo de resposta"
                  $isRequired
                  $errors={fieldErrors[typeId]}
                  $options={QUESTION_TYPES}
                  value={question.type}
                  onChange={(e) => e.target.name === typeId && updateQuestion(question.key, { type: e.target.value })}
                />
                <GroupError message={fieldErrors[typeId]} />
              </Stack>
            </FullWidth>
            {question.type === "multi" ? renderOptions(question) : null}
            {renderRemoveQuestion(question)}
          </FormBox>
        );
      })}

      <AddQuestion>
        <Button $text="Adicionar pergunta" $variant="outline" $action={addQuestion} $noWidth />
      </AddQuestion>
    </FormPage>
  );
}
